import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SupplierDashboard from "@/pages/SupplierDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import DriverIntakePage from './components/fleet/DriverIntakePage';
import InternalDashboard from "@/pages/internal/InternalDashboard";
import DriverApp from "@/pages/driver/DriverApp";
import DriverMaintenance from "@/pages/driver/DriverMaintenance";
import FleetApp from "@/pages/driver/FleetApp";
import DispatcherApp from "@/pages/dispatcher/DispatcherApp";
import TripChatPage from "@/pages/trip/TripChatPage";
import TemporaryDriverTripChatPage from "@/pages/trip/TemporaryDriverTripChatPage";
import StandaloneSignboardPage from "@/pages/StandaloneSignboardPage";
import PublicBookingEntryPage from "@/pages/PublicBookingEntryPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicBookingLookupPage from "@/pages/PublicBookingLookupPage";
import PublicBookingPaymentPage from "@/pages/PublicBookingPaymentPage";
import PublicReviewPage from "@/pages/PublicReviewPage";
import PublicMemberMyPage from "@/pages/PublicMemberMyPage";
import CustomerSupportWidget from "@/components/CustomerSupportWidget";
import { API_BASE_URL as API } from "./apiConfig";

const ADMIN_AUTH_BOOT_TIMEOUT_MS = 8000;
const ADMIN_BOOT_DIAGNOSTIC_KEY = "shonan_admin_boot_diagnostic";

const reportAdminBootPhase = (phase, detail = "") => {
  // The native boot screen writes `ready` after React has rendered. Keep that
  // terminal proof intact when later background authentication completes, so a
  // physical-device release check can distinguish a rendered page from a
  // process that merely launched.
  if (phase !== "ready") {
    try {
      const existingDiagnostic = JSON.parse(localStorage.getItem(ADMIN_BOOT_DIAGNOSTIC_KEY) || "null");
      if (existingDiagnostic?.phase === "ready") return;
    } catch {
      // A malformed or unavailable diagnostic should never block startup.
    }
  }
  window.__SHONAN_ADMIN_BOOT__?.setPhase?.(phase, detail);
};

const clearStoredAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

class AdminAppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Admin app render failed:", error, info);
    window.__SHONAN_ADMIN_BOOT__?.showError?.(
      "管理员 App 页面出错",
      error?.stack || error?.message || String(error),
    );
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-black px-5 py-8 text-white">
          <div className="mx-auto max-w-md rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
            <h1 className="text-lg font-semibold">管理员 App 出错了</h1>
            <p className="mt-2 text-sm text-red-100">
              {this.state.error?.message || "未知错误"}
            </p>
            <button
              type="button"
              className="mt-4 min-h-12 w-full bg-amber-200 px-4 py-3 font-semibold text-black"
              onClick={() => window.location.reload()}
            >
              重新启动
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
const getBasename = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  // /proxy/41343/... -> basename = /proxy/41343
  if (parts[0] === "proxy" && parts[1]) return `/${parts[0]}/${parts[1]}`;
  return "/";
};

const isCapacitorRuntime = () => {
  if (typeof window === "undefined") {
    return false;
  }
  if (
    process.env.REACT_APP_CAPACITOR_APP_ROLE === "admin" ||
    process.env.REACT_APP_CAPACITOR_APP_ROLE === "dispatcher"
  ) {
    return true;
  }
  return window.location?.protocol === "capacitor:" || !!window.Capacitor?.isNativePlatform?.();
};

const getCapacitorAppRole = () => {
  if (process.env.REACT_APP_CAPACITOR_APP_ROLE === "admin") {
    return "admin";
  }
  if (process.env.REACT_APP_CAPACITOR_APP_ROLE === "dispatcher") {
    return "dispatcher";
  }
  return "driver";
};

const configureCapacitorViewport = () => {
  if (!isCapacitorRuntime()) {
    return;
  }
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );
  }
  document.documentElement.classList.add("capacitor-ios-app");
  const appRole = getCapacitorAppRole();
  document.documentElement.classList.add(`capacitor-${appRole}-app`);
};

const installAdminHorizontalSwipeBlocker = () => {
  if (!isCapacitorRuntime() || getCapacitorAppRole() !== "admin") {
    return () => {};
  }

  let startX = 0;
  let startY = 0;
  let blockingHorizontalSwipe = false;

  const reset = () => {
    startX = 0;
    startY = 0;
    blockingHorizontalSwipe = false;
  };

  const handleTouchStart = (event) => {
    if (event.touches?.length !== 1) {
      reset();
      return;
    }
    const touch = event.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    blockingHorizontalSwipe = false;
  };

  const handleTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch || event.touches.length !== 1) return;

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const horizontal = Math.abs(deltaX);
    const vertical = Math.abs(deltaY);

    if (horizontal > 14 && horizontal > vertical * 1.12) {
      blockingHorizontalSwipe = true;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleTouchEnd = (event) => {
    if (blockingHorizontalSwipe) {
      event.preventDefault();
      event.stopPropagation();
    }
    reset();
  };

  window.addEventListener("touchstart", handleTouchStart, { capture: true, passive: true });
  window.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
  window.addEventListener("touchend", handleTouchEnd, { capture: true, passive: false });
  window.addEventListener("touchcancel", handleTouchEnd, { capture: true, passive: false });

  return () => {
    window.removeEventListener("touchstart", handleTouchStart, { capture: true });
    window.removeEventListener("touchmove", handleTouchMove, { capture: true });
    window.removeEventListener("touchend", handleTouchEnd, { capture: true });
    window.removeEventListener("touchcancel", handleTouchEnd, { capture: true });
  };
};

function PublicBookingRootPage() {
  return <PublicBookingEntryPage />;
}

function RouteAwareCustomerSupportWidget() {
  const location = useLocation();
  const hiddenPrefixes = ["/internal", "/driver", "/fleet"];
  if (hiddenPrefixes.some((prefix) => location.pathname.startsWith(prefix))) return null;
  return <CustomerSupportWidget />;
}

function ForcePasswordChange({onChanged}){const [currentPassword,setCurrent]=useState(""),[newPassword,setNew]=useState(""),[error,setError]=useState("");const submit=async e=>{e.preventDefault();try{const r=await axios.put(`${API}/auth/password`,{current_password:currentPassword,new_password:newPassword});localStorage.setItem("token",r.data.access_token);const me=await axios.get(`${API}/auth/me`);localStorage.setItem("user",JSON.stringify(me.data));onChanged(me.data)}catch(x){setError(x.response?.data?.detail||"密码修改失败")}};return <form onSubmit={submit} className="mx-auto mt-12 max-w-md premium-card p-6"><h1 className="text-xl font-semibold">首次登录，请修改密码</h1>{error&&<p className="text-destructive">{error}</p>}<input className="premium-input mt-4" type="password" placeholder="临时密码" value={currentPassword} onChange={e=>setCurrent(e.target.value)} required/><input className="premium-input mt-4" type="password" minLength={8} placeholder="新密码（至少8位）" value={newPassword} onChange={e=>setNew(e.target.value)} required/><button className="premium-button mt-4 w-full bg-primary text-primary-foreground">修改并进入后台</button></form>}

function AdminCapacitorApp({ currentUser, isAuthenticated, onLogin, onLogout,onUserUpdate }) {
  return (
    <AdminAppErrorBoundary>
      <div className="admin-capacitor-app">
        <BrowserRouter basename={getBasename()}>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated() && currentUser?.role === "admin" ? (
                  <Navigate to="/" replace />
                ) : (
                  <LoginPage onLogin={onLogin} appMode="admin" />
                )
              }
            />
            <Route
              path="/*"
              element={
                !isAuthenticated() || currentUser?.role !== "admin" ? (
                  <Navigate to={{ pathname: '/login', search: window.location.search }} replace />
                ) : currentUser?.must_change_password ? <ForcePasswordChange onChanged={onUserUpdate}/> : (
                  <AdminDashboard user={currentUser} onLogout={onLogout} appMode="admin-app" />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AdminAppErrorBoundary>
  );
}


// Axios interceptor to add token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    configureCapacitorViewport();
    const uninstallAdminHorizontalSwipeBlocker = installAdminHorizontalSwipeBlocker();
    reportAdminBootPhase("auth-start", "Authentication bootstrap started");
    checkAuth();
    return uninstallAdminHorizontalSwipeBlocker;
  }, []);

  useEffect(() => {
    if (!loading && isCapacitorRuntime() && getCapacitorAppRole() === "admin") {
      reportAdminBootPhase("ready", "Admin route can render");
      window.dispatchEvent(new Event("shonan-admin-app-ready"));
    }
  }, [loading]);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (!token) {
      setUser(null);
      setLoading(false);
      reportAdminBootPhase("auth-no-session", "No saved login token");
      return;
    }

    let restoredCachedUser = false;
    if (savedUser) {
      try {
        const cachedUser = JSON.parse(savedUser);
        if (!cachedUser || typeof cachedUser !== "object") {
          throw new Error("Saved user is invalid");
        }
        setUser(cachedUser);
        restoredCachedUser = true;
        setLoading(false);
        reportAdminBootPhase("auth-cache-restored", "Saved admin session restored immediately");
      } catch (storageError) {
        localStorage.removeItem("user");
        reportAdminBootPhase("auth-cache-invalid", storageError.message);
      }
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        timeout: ADMIN_AUTH_BOOT_TIMEOUT_MS,
      });
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      reportAdminBootPhase("auth-verified", "Saved login verified");
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        clearStoredAuth();
        setUser(null);
        reportAdminBootPhase("auth-rejected", `Authentication rejected with ${status}`);
      } else if (!restoredCachedUser) {
        setUser(null);
        reportAdminBootPhase("auth-unavailable", error.message);
      } else {
        reportAdminBootPhase("auth-deferred", error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = async () => {
    try{await axios.post(`${API}/auth/logout`)}catch{}
    clearStoredAuth();
    localStorage.removeItem("internal_token");
    localStorage.removeItem("internal_user");
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    const newUser = { ...user, ...updatedUser };
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  };

  // 检查是否已登录（优先从 localStorage，解决 state 异步更新问题）
  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    return !!(token && savedUser);
  };

  // 获取当前用户（优先从 state，否则从 localStorage）
  const getCurrentUser = () => {
    if (user) return user;
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black px-6 text-center text-white">
        <div>
          <div className="text-xl font-semibold text-amber-200">SHONAN ADMIN</div>
          <div className="mt-2 text-sm text-white/70">正在验证登录，最多等待 8 秒...</div>
        </div>
      </div>
    );
  }

  if (isCapacitorRuntime()) {
    const currentUser = getCurrentUser();
    if (getCapacitorAppRole() === "admin") {
      return (
        <LanguageProvider>
          <AdminCapacitorApp
            currentUser={currentUser}
            isAuthenticated={isAuthenticated}
            onLogin={handleLogin}
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}
          />
        </LanguageProvider>
      );
    }
    if (getCapacitorAppRole() === "dispatcher") {
      return (
        <LanguageProvider>
          <div className="App">
            <BrowserRouter basename={getBasename()}>
              <Routes>
                <Route path="/dispatcher/*" element={<DispatcherApp />} />
                <Route path="*" element={<Navigate to="/dispatcher" replace />} />
              </Routes>
            </BrowserRouter>
          </div>
        </LanguageProvider>
      );
    }
    return (
      <LanguageProvider>
        <div className="App">
          <BrowserRouter basename={getBasename()}>
            <Routes>
              <Route path="/driver/maintenance" element={<DriverMaintenance />} />
              <Route path="/driver/*" element={<DriverApp />} />
              <Route path="/fleet/*" element={<FleetApp />} />
              <Route path="*" element={<Navigate to="/driver" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    );
  }

  const currentUser = getCurrentUser();
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter basename={getBasename()}>
          <Routes>
            <Route path="/driver-information/:token" element={<DriverIntakePage />} />
            <Route
              path="/login"
              element={
                isCapacitorRuntime() ? (
                  <Navigate to="/driver" replace />
                ) : isAuthenticated() ? (
                  <Navigate to="/" replace />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              }
            />
            <Route
              path="/register"
              element={
                isAuthenticated() ? <Navigate to="/" replace /> : <RegisterPage />
              }
            />
            <Route
              path="/book/ja"
              element={<PublicBookingEntryPage />}
            />
            <Route
              path="/book"
              element={<PublicBookingRootPage />}
            />
            <Route
              path="/book/guest"
              element={<PublicBookingPage initialBookingMode="quick" lockedBookingMode />}
            />
            <Route
              path="/book/member"
              element={<PublicBookingPage initialBookingMode="member" lockedBookingMode />}
            />
            <Route
              path="/book/mypage"
              element={<PublicMemberMyPage />}
            />
            <Route
              path="/booking/check"
              element={<PublicBookingLookupPage />}
            />
            <Route
              path="/booking/payment"
              element={<PublicBookingPaymentPage />}
            />
            <Route
              path="/booking/review"
              element={<PublicReviewPage />}
            />
            <Route
              path="/"
              element={
                isCapacitorRuntime() ? (
                  <Navigate to="/driver" replace />
                ) : !isAuthenticated() ? (
                  <Navigate to={{ pathname: '/login', search: window.location.search }} replace />
                ) : currentUser?.role==="admin"&&currentUser?.must_change_password ? <ForcePasswordChange onChanged={handleUserUpdate}/> : ["admin", "finance"].includes(currentUser?.role) ? (
                  <AdminDashboard user={currentUser} onLogout={handleLogout} />
                ) : (
                  <SupplierDashboard user={currentUser} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
                )
              }
            />
            <Route
              path="/internal"
              element={<InternalDashboard />}
            />
            <Route
              path="/trip/:orderId/chat"
              element={<TripChatPage />}
            />
            <Route
              path="/trip/:orderId/driver-chat"
              element={<TemporaryDriverTripChatPage />}
            />
            <Route
              path="/signboard"
              element={<StandaloneSignboardPage />}
            />
            {/* 司机端路由 - 独立认证系统 */}
            <Route path="/driver/maintenance" element={<DriverMaintenance />} />
            <Route
              path="/driver/*"
              element={<DriverApp />}
            />
            <Route path="/fleet/*" element={<FleetApp />} />
            <Route path="/dispatcher/*" element={<DispatcherApp />} />
          </Routes>
          <RouteAwareCustomerSupportWidget />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
